<template>
  <div class="w-full">
    <div ref="windowEl" class="w-full overflow-hidden rounded-[15px] border border-[#45454b] bg-[#1b1b1f]" :style="{ height: `${DEMO_HEIGHT * scale}px` }">
      <!-- Laid out at a fixed 500×340 and scaled to fit, so note positions and the drag keyframes stay in sync at any width -->
      <div
        ref="demoEl"
        role="group"
        :aria-label="t('label')"
        class="relative h-[340px] w-[500px] origin-top-left overflow-hidden bg-[#141416] text-[#ededf2]"
        :style="{ transform: `scale(${scale})` }"
      >
        <div class="absolute inset-y-0 left-0 w-32 border-r border-[#333338] bg-[#242428] px-3 py-3.5 text-[13px] font-bold">
          {{ t('shared') }}
          <span class="float-right text-[#e77c44]" aria-hidden="true">＋</span>
        </div>
        <div
          class="absolute inset-y-0 left-32 right-0 bg-[linear-gradient(#2b2b30_1px,transparent_1px),linear-gradient(90deg,#2b2b30_1px,transparent_1px)] bg-[length:22px_22px]"
        ></div>
        <div class="absolute left-[142px] top-[44px] grid gap-1.5" aria-hidden="true">
          <span
            v-for="avatar in avatars"
            :key="avatar.initial"
            class="grid h-[26px] w-[26px] place-items-center rounded-full text-[11px] font-bold text-[#211825]"
            :class="avatar.bg"
          >
            {{ avatar.initial }}
          </span>
        </div>

        <div v-for="note in notes" :key="note.name" :class="[noteClass, note.position]">
          <span :class="[nameClass, note.bg]">{{ note.name }}</span>
          {{ t(note.text) }}
        </div>

        <button
          ref="cardEl"
          type="button"
          class="drag-demo z-10 w-[118px] cursor-grab touch-none select-none border-0 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-tw-orange-cta"
          :class="[noteClass, { paused: !playing && !manual }]"
          :style="manual ? { left: `${position.x}px`, top: `${position.y}px`, animation: 'none', opacity: 1 } : undefined"
          aria-describedby="hero-animation-hint"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="dragging = false"
          @pointercancel="dragging = false"
          @lostpointercapture="dragging = false"
          @keydown="onKeyDown"
        >
          <span :class="[nameClass, 'bg-[#6fcf4a]']">Noah</span>
          {{ t('noah') }}
        </button>
        <span id="hero-animation-hint" class="sr-only">{{ t('hint') }}</span>
        <span class="absolute bottom-3 right-3 text-[11px] text-[#aaa]" aria-hidden="true">84%</span>
      </div>
    </div>
    <!--div class="mt-3 flex justify-end">
      <button
        type="button"
        class="rounded-[5px] border border-[#777780] px-2 py-1 text-xs text-[#c8c8cf] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-tw-orange-cta motion-reduce:hidden"
        @click="togglePlay"
      >
        {{ playing ? t('pause') : t('play') }}
      </button>
      </div-->
  </div>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, reactive, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  const DEMO_WIDTH = 500
  const DEMO_HEIGHT = 340
  const START = { x: 14, y: 54 }
  const KEY_STEP = 10

  const noteClass = 'absolute rounded-lg bg-[#3a3a40] px-[9px] py-2 text-[11px] leading-[1.4] text-[#b8b8c0]'
  const nameClass = 'mb-1.5 table rounded-[5px] px-[7px] py-0.5 text-[10px] font-bold text-[#251529]'

  const avatars = [
    { initial: 'S', bg: 'bg-[#e86a9a]' },
    { initial: 'J', bg: 'bg-[#d9c23e]' },
    { initial: 'A', bg: 'bg-[#9b6bde]' },
  ]

  const notes = [
    { name: 'Sara', text: 'sara', bg: 'bg-[#e86a9a]', position: 'left-[198px] top-[22px] w-[122px]' },
    { name: 'Jonas', text: 'jonas', bg: 'bg-[#d9c23e]', position: 'left-[318px] top-[150px] w-[122px]' },
    { name: 'Amina', text: 'amina', bg: 'bg-[#9b6bde]', position: 'left-[348px] top-[40px] w-[110px]' },
  ]

  const windowEl = ref<HTMLElement>()
  const demoEl = ref<HTMLElement>()
  const cardEl = ref<HTMLElement>()

  const scale = ref(1)
  const playing = ref(true)
  // Manual mode: the visitor has grabbed the card, so the loop stops and the card stays where they left it
  const manual = ref(false)
  const dragging = ref(false)
  const position = reactive({ ...START })
  const grabOffset = { x: 0, y: 0 }

  let observer: ResizeObserver | undefined

  onMounted(() => {
    observer = new ResizeObserver(() => {
      if (windowEl.value) scale.value = windowEl.value.clientWidth / DEMO_WIDTH
    })
    if (windowEl.value) observer.observe(windowEl.value)
  })

  onBeforeUnmount(() => observer?.disconnect())

  // Take over from the animation at the card's current on-screen position
  function freeze(): void {
    if (manual.value || !cardEl.value || !demoEl.value) return
    const card = cardEl.value.getBoundingClientRect()
    const demo = demoEl.value.getBoundingClientRect()
    position.x = (card.left - demo.left) / scale.value
    position.y = (card.top - demo.top) / scale.value
    manual.value = true
    playing.value = false
  }

  function moveTo(x: number, y: number): void {
    const card = cardEl.value
    if (!card) return
    position.x = Math.max(0, Math.min(DEMO_WIDTH - card.offsetWidth, x))
    position.y = Math.max(0, Math.min(DEMO_HEIGHT - card.offsetHeight, y))
  }

  function onPointerDown(e: PointerEvent): void {
    freeze()
    dragging.value = true
    const card = (e.currentTarget as HTMLElement).getBoundingClientRect()
    grabOffset.x = (e.clientX - card.left) / scale.value
    grabOffset.y = (e.clientY - card.top) / scale.value
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  function onPointerMove(e: PointerEvent): void {
    if (!dragging.value || !demoEl.value) return
    const demo = demoEl.value.getBoundingClientRect()
    moveTo((e.clientX - demo.left) / scale.value - grabOffset.x, (e.clientY - demo.top) / scale.value - grabOffset.y)
  }

  function onKeyDown(e: KeyboardEvent): void {
    const steps: Record<string, [number, number]> = {
      ArrowLeft: [-KEY_STEP, 0],
      ArrowRight: [KEY_STEP, 0],
      ArrowUp: [0, -KEY_STEP],
      ArrowDown: [0, KEY_STEP],
    }
    const step = steps[e.key]
    if (!step) return
    e.preventDefault()
    freeze()
    moveTo(position.x + step[0], position.y + step[1])
  }

  function togglePlay(): void {
    if (manual.value) {
      manual.value = false
      Object.assign(position, START)
      playing.value = true
      return
    }
    playing.value = !playing.value
  }

  const { t } = useI18n({
    messages: {
      no: {
        label: 'Animert eksempel på Samtavla',
        shared: 'Felles',
        sara: 'Alle kan skrive sine egne tanker her',
        jonas: 'Vi bygger videre på hverandres idéer',
        amina: 'Ingen innlogging, bare en PIN-kode',
        noah: 'Dra kortet mitt for å prøve selv!',
        hint: 'Dra kortet, eller flytt det med piltastene.',
        pause: 'Pause',
        play: 'Spill av',
      },
      en: {
        label: 'Animated example of Talkwall',
        shared: 'Shared',
        sara: 'Everyone can write their own thoughts here',
        jonas: "We build on each other's ideas",
        amina: 'No login, just a PIN code',
        noah: 'Drag my card to try it yourself!',
        hint: 'Drag the card, or move it with the arrow keys.',
        pause: 'Pause',
        play: 'Play',
      },
    },
  })
</script>

<style scoped>
  .drag-demo {
    left: 14px;
    top: 54px;
    animation: tw-drag 7s ease-in-out infinite;
  }

  .drag-demo.paused {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    .drag-demo {
      animation: none;
    }
  }

  @keyframes tw-drag {
    0% {
      opacity: 1;
      transform: translate(0, 0) scale(1);
    }
    10% {
      opacity: 1;
      transform: translate(0, 0) scale(1.03);
    }
    45% {
      opacity: 1;
      transform: translate(204px, 61px) scale(1.03);
    }
    58%,
    85% {
      opacity: 1;
      transform: translate(204px, 61px) scale(1);
    }
    96% {
      opacity: 0;
      transform: translate(204px, 61px) scale(1);
    }
    100% {
      opacity: 0;
      transform: translate(0, 0) scale(1);
    }
  }
</style>
